import { defineStore, acceptHMRUpdate, getActivePinia } from 'pinia'
import { watch } from 'vue'
import { useFirestore } from 'vuefire'
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { Task, type TaskProgressStatus } from '@/models/Task'
import { ProjectTask } from '@/models/ProjectTask'
import { taskNeverAccepted } from '@/models/taskLifecycle'
import { parseRevision, REVISIONS_COLLECTION } from '@/models/TaskRevision'
import { reconcilePending } from '@/composables/useTaskPending'
import { recoverPendingCommands } from '@/composables/useTaskRecovery'
import { submitPendingCommand } from '@/composables/useTaskPending'
import { reportOutcome } from '@/composables/useTaskCommandErrors'
import { isNotQueued } from '@/composables/useTaskCommands'

/**
 * One field change, and everything the caller has to supply for the queued
 * version to be safe to render (IR-126 §8).
 */
interface FieldEditArgs {
  task: Task
  field: string
  after: unknown
  /** Puts the optimistic value on screen. Called before the write. */
  apply: () => void
  /** Puts the previous value back. Called only on a refusal. */
  rollback: () => void
  /** Turns a stored value into something a person recognises (§4.5). */
  label: (value: unknown) => string
  /**
   * Run when the change actually applied — and on a **resubmit** too.
   *
   * The reason this is a callback rather than something the caller does
   * with the return value: a `stale` conflict resolved by the user calls
   * back into here, not into the caller, so anything the caller did after
   * `await` would be skipped for exactly the moves that took two attempts.
   * CSM tracking is the thing that was being skipped, including the
   * `task_completed` milestone.
   */
  onApplied?: () => void
    }

/** One field of a submission — its stored path, its new value, and how to
 * name a value of it to a person (§4.5). */
interface EditedField {
  field: string
  after: unknown
  label: (value: unknown) => string
}

/**
 * Several fields submitted as ONE command (IR-126 §4.4, phase 5.1).
 *
 * {@link FieldEditArgs} with `field`/`after`/`label` widened to a list, and the
 * shape everything below actually runs on — a single-field edit is the
 * one-element case, not a second path.
 *
 * **One command rather than one per field is a correctness requirement, not a
 * saving.** `base_seq` is task-level: the first command's revision moves
 * `last_revision_seq`, so a second command composed against the seq the editor
 * opened on would be refused as `stale` — the user's own edit conflicting with
 * itself, on a field nobody else touched. It also makes the edit atomic, which
 * is what lets the trail record one authored change rather than three.
 */
interface FieldsEditArgs {
  task: Task
  changes: EditedField[]
  /** Puts the optimistic values on screen. Called before the write. */
  apply: () => void
  /** Puts the previous values back. Called only on a refusal. */
  rollback: () => void
  onApplied?: () => void
}

/**
 * A `stale` refusal the user has to resolve (IR-126 §5.1, §4.5).
 *
 * Both values are already **display labels** rather than stored ones: a
 * conflict between "B" and "C" is not a choice anyone can make. `resubmit`
 * re-sends the change composed against the seq the task is on now — offered
 * only because a human looked at both values, which is the whole distance
 * between this and the auto-rebase §4.5 removed `SAME_FIELD_CONFLICT` to avoid.
 */
/**
 * The value at a dotted leaf path in a raw task document.
 *
 * The server's `read_stored_path` (`apply.py`) by another name, and
 * deliberately the same reading: a conflict shows what the *document* holds,
 * so it walks the raw snapshot rather than a parsed model. `undefined` for a
 * missing intermediate is correct — the field genuinely has no value — and is
 * distinct from a flat lookup returning `undefined` because it never descended.
 */
export function readTaskPath(
  data: Record<string, unknown> | undefined,
  path: string,
): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (node, key) => (node as Record<string, unknown> | undefined)?.[key],
      data,
    )
}

/**
 * Put a value at a dotted leaf path — {@link readTaskPath}'s mirror.
 *
 * The optimistic half of a content edit: the board's copy of the task has to
 * show the new text before the command settles, exactly as a priority change
 * does.
 *
 * **Missing intermediates are created.** A task whose `context` was never
 * written can still be given a finding, and the first edit of an empty field
 * looking like it did nothing is the vanished-edit failure in miniature. What
 * that costs is a rollback obligation the caller owns — see
 * `updateTaskContent`, which deletes a map it created rather than leaving an
 * empty one behind.
 */
export function writeTaskPath(
  root: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const keys = path.split('.')
  const leaf = keys.pop() as string
  let node = root
  for (const key of keys) {
    const next = node[key]
    if (typeof next !== 'object' || next === null) node[key] = {}
    node = node[key] as Record<string, unknown>
  }
  node[leaf] = value
}

/**
 * Stored leaf path → the property that actually HOLDS it on a `Task`.
 *
 * The command speaks stored paths (`documents_v2`) because that is
 * `EditPolicy`'s vocabulary. The optimistic LOCAL write cannot: `Task` exposes
 * those two as **getter-only** accessors over `documentsV2` / `clausesV2`, and
 * assigning to a getter through Vue's reactive proxy makes its set trap return
 * `false` — a `TypeError` in strict mode, thrown before the command is even
 * written (MYAC-1109, observed on dev: "'set' on proxy: trap returned falsish
 * for property 'documents_v2'").
 *
 * The bug it caused was worse than a failed edit: the throw happened ahead of
 * the write, so nothing was queued, while the user was told the change "may
 * still go through". Two vocabularies that looked identical for every scalar
 * field, and diverged for the first list one.
 *
 * Only fields `EditPolicy` makes editable appear here. The other getters
 * (`source_questions`, `source_requirements`) are deliberately absent: nothing
 * may write them, and an entry would imply otherwise.
 */
const OPTIMISTIC_TARGET: Readonly<Record<string, string>> = {
  documents_v2: 'documentsV2',
  clauses_v2: 'clausesV2',
}

/** Where the optimistic pair reads and writes. See {@link OPTIMISTIC_TARGET}. */
export function optimisticPath(field: string): string {
  return OPTIMISTIC_TARGET[field] ?? field
}

/**
 * A value in the shape Firestore will accept inside a command payload.
 *
 * Two things this fixes, both of which have shipped and both of which come from
 * the same root: a task's content is read back off the resolved `Task`, and
 * `plainToInstance` makes those CLASS INSTANCES with every `@Expose()`d
 * property assigned — including the optional ones, as `undefined`.
 *
 * - `addDoc` refuses a class instance outright: "Unsupported field value: a
 *   custom Tw object". The document tick copied its rows with `{ ...d }`, which
 *   flattens the row but leaves `actions` full of `V2DocumentAction` instances,
 *   because spread is shallow (observed on dev, MYAC-1109).
 * - `addDoc` refuses `undefined` too, since the app builds Firestore with no
 *   `ignoreUndefinedProperties`.
 *
 * Done HERE rather than at each call site because this is the boundary every
 * content edit crosses on its way to `command_queues`, and fixing it per-caller
 * is how the same bug arrived twice in two different fields. `Date` is passed
 * through: Firestore accepts it, and rebuilding one would destroy it.
 */
export function toCommandValue(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value
  if (value instanceof Date) return value
  if (Array.isArray(value)) return value.map(toCommandValue)

  const out: Record<string, unknown> = {}
  for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
    if (inner === undefined) continue
    out[key] = toCommandValue(inner)
  }
  return out
}

/** One field's side of a `stale` refusal — the row the dialog renders. */
export interface TaskConflictField {
  /**
   * The dotted path the command carried (`context.finding`), which is also the
   * key into the policy's label map. Kept rather than pre-resolved to a label
   * so the dialog owns presentation and the store owns the data.
   */
  field: string
  /** What the task holds now — read fresh, through the path. */
  stored: string
  /** What the user tried to set, already rolled back off the screen. */
  attempted: string
}

export interface TaskConflict {
  taskId: string
  /**
   * One entry per field the refused command carried (§5.1, card 5.1).
   *
   * Was a single `stored`/`attempted` pair, which was the whole conflict while
   * 3.4–3.6 were the only callers — progress, priority and assignee each move
   * one field. The editing surface submits several at once, and a refusal that
   * shows the first of them tells the user their finding is in conflict while
   * silently dropping the risk they also changed.
   */
  fields: TaskConflictField[]
  resubmit: () => Promise<void>
}
import { TaskGroupingSubject } from '@/models/TaskGroupingSubject'
import {
  getTaskPrioritySortWeight,
  TASK_PRIORITY_TO_KEY,
} from '@/components/shared/priority/tokens'
import { assessmentIdFromPath, taskAssessmentId } from '@/utils/taskSourceOfTruth'
import { logger } from '@/utils/logger'
import { trackAction } from '@/composables/useActivityTracker'
import { assigneeLabel, labelForField, priorityLabel } from '@/models/taskFieldLabels'
import { CsmAction } from '@/constants/csmActions'
import i18n from '@/plugins/i18n'

const t = i18n.global.t
const d = i18n.global.d

/**
 * The three value labellers now live in `@/models/taskFieldLabels`, imported
 * above. They moved when the revision trail (5.4) became a second reader: it
 * renders `before`/`after` for whatever field a revision names, so it needs a
 * lookup by field path rather than a hand-picked function — and two readers of
 * the same labels must not be two copies of them.
 */

/**
 * The Inactive column — a LIFECYCLE bucket, not a progress value (IR-126 §4.3,
 * 5.3).
 *
 * Deliberately not a `TaskProgressStatus`. Nothing writes `inactive` to
 * `review_progress`, and a task's progress survives being sent back untouched,
 * which is what lets re-approve drop it straight back where it was.
 */
export const INACTIVE_COLUMN = 'inactive'

/** A board column: the three progress columns plus {@link INACTIVE_COLUMN}. */
export type KanbanColumn = TaskProgressStatus | typeof INACTIVE_COLUMN

/** Columns displayed on the kanban board (in order) */
export const KANBAN_COLUMNS: KanbanColumn[] = [
  'todo',
  'in_progress',
  'done',
  INACTIVE_COLUMN,
]

/** The progress columns a card may be dragged between. */
export const PROGRESS_COLUMNS: TaskProgressStatus[] = ['todo', 'in_progress', 'done']

/**
 * Why a task is in the Inactive column, as three distinct outcomes (§4.3).
 *
 * **Three, not two.** `task_unapprove` writes `review_decision: pending |
 * rejected` alongside `lifecycle: unapproved`, so one lifecycle carries two
 * meanings a user must be able to tell apart — a task waiting for a decision
 * and one that was turned down.
 */
export type InactiveReason = 'sent_back' | 'rejected' | 'retired' | 'unknown'

/**
 * Which column a task belongs in — **lifecycle first, progress second**.
 *
 * `null` means "not on the board at all", which today is only the legacy
 * `cancelled` progress value. That exclusion used to happen by accident: the
 * caller looked the progress up in a pre-seeded object and anything missing
 * fell through silently. Stating it here makes it a decision, and means a
 * derived column that is simply not seeded can no longer vanish the same way —
 * which is exactly how the Inactive column would have disappeared.
 *
 * **This rule lives here and nowhere else.** `columnCounts` reads straight off
 * `tasksByColumn`, so a second copy of it in a component would show a column
 * holding one set of cards under a header counting another — the two-definitions
 * failure MYAC-992 spent a whole card removing.
 */
export function kanbanColumnFor(task: Task): KanbanColumn | null {
  if (!task.isActiveLifecycle) return INACTIVE_COLUMN
  const progress = task.effectiveProgress
  return PROGRESS_COLUMNS.includes(progress) ? progress : null
}

/**
 * The label an inactive card carries, or `null` for an active one.
 *
 * `archived` is **"retired"**, and deliberately not "done" or "finished with".
 * `task_archive` retires a task, typically a manual one, and a task can be
 * archived without ever having been started — so putting it in Done, or calling
 * it finished, would have the product assert work that may never have happened.
 * On an audit remediation board that is a false statement, not a wording choice.
 */
export function inactiveReasonFor(task: Task): InactiveReason | null {
  if (task.isActiveLifecycle) return null
  if (task.isArchivedLifecycle) return 'retired'
  if (task.lifecycle !== 'unapproved') {
    // A lifecycle this build has never heard of. `kanbanColumnFor` already
    // declines to guess where it belongs; declining to guess WHY is the same
    // caution carried one step further. Reading the decision here would label a
    // task "Sent back" that nobody sent back — a plausible-but-wrong label,
    // which is worse than an honest vague one because a user acts on it.
    return 'unknown'
  }
  return task.effectiveDecision === 'rejected' ? 'rejected' : 'sent_back'
}

export type KanbanFiltersState = {
  priority: string | 'all'
  assignee: 'all' | 'me' | string
  assessmentIds: string[]
  document: string | 'all'
  /**
   * K4 (MYAC-776) — which ENGINE produced the task (`ProjectTask.source.track`).
   *
   * The gap analysis creates tasks in bulk from the specialist chat — 26 of them
   * on one run — and the board they land on already holds a hundred cards, so
   * "the tasks this analysis produced" was not a question the board could
   * answer. Driven from the URL (`?source=gap_analysis&gap=…`), so the link
   * that creates them is also the link that finds them.
   */
  sourceTrack: string | 'all'
  /** …and which analysis, when the caller names one. `''` = every analysis of
   *  that track. */
  sourceGapId: string
}

export const DEFAULT_KANBAN_FILTERS: KanbanFiltersState = {
  priority: 'all',
  assignee: 'all',
  assessmentIds: [],
  document: 'all',
  sourceTrack: 'all',
  sourceGapId: '',
}

/**
 * The filters a freshly loaded project starts with.
 *
 * Every filter chosen in this UI goes back to its default — a project must not
 * inherit the previous one's priority or assignee. The provenance filter is the
 * exception because nobody chose it here: `TasksPage` sets it from the URL the
 * moment it mounts, and the parent view starts the load from its own `onMounted`
 * — which runs AFTER the child's — so a reset here erased the filter the link
 * had just asked for, and the board opened on all 140 cards with no chip.
 * `TasksPage` re-applies (or clears) it on every route change, so the URL, not
 * this reset, owns it. (On the QMS route the guard starts the load before
 * `TasksPage` exists, so the order is the reverse — and the outcome the same:
 * that immediate watch re-asserts the filter after mount either way.)
 */
export function kanbanFiltersForNewProject(previous: KanbanFiltersState): KanbanFiltersState {
  return {
    ...DEFAULT_KANBAN_FILTERS,
    sourceTrack: previous.sourceTrack,
    sourceGapId: previous.sourceGapId,
  }
}

export type SubjectTreeItem =
  | { kind: 'real'; path: string; subject: TaskGroupingSubject; count: number }
  | { kind: 'other'; path: string; count: number }

export type SubjectTreeGroup = {
  assessmentId: string
  assessmentLabel: string
  subjects: SubjectTreeItem[]
}

export const OTHER_SUBJECT_KEY_PREFIX = 'other:'

export function otherSubjectKey(assessmentId: string): string {
  return `${OTHER_SUBJECT_KEY_PREFIX}${assessmentId}`
}

function sortTasksByPriority(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const weightDiff = getTaskPrioritySortWeight(a.priority) - getTaskPrioritySortWeight(b.priority)
    if (weightDiff !== 0) return weightDiff
    const aTime = a.created_at?.getTime?.() ?? 0
    const bTime = b.created_at?.getTime?.() ?? 0
    return aTime - bTime
  })
}

function extractTaskId(taskPath: string | null | undefined): string | null {
  if (!taskPath) return null
  const match = taskPath.match(/\/tasks\/([^/]+)$/)
  return match ? match[1] : null
}

function extractAssessmentIdFromSubjectPath(subjectPath: string): string | null {
  const match = subjectPath.match(/\/assessments\/([^/]+)\//)
  return match ? match[1] : null
}

/**
 * The board store, under a given id.
 *
 * A factory so the cross-project board (MYAC-1268) can run ONE OF THESE PER
 * PROJECT, each an ordinary single-project board — rather than teaching this
 * store to hold several projects. That keeps every rule it enforces
 * (pointer-task resolution, the revision anchor edits compose against,
 * pending commands, conflicts) exactly as it is, instead of re-implementing
 * them; re-implementing the board's read once is what the MYAC-1268 product
 * review traced three defects to. Nothing in this store, nor in the
 * composables its edit path uses, calls `useTasksStore()` back — which is what
 * makes several instances safe.
 */
/** No content field edited — one shared empty set, so a getter allocates nothing. */
const NO_EDITS: ReadonlySet<string> = new Set()

function defineTasksStore(id: string) {
  return defineStore(id, {
  state: () => ({
    isLoading: false,
    _current_pid: '' as string,
    _resolvedTasks: new Map<string, Task>(),
    _collectionUnsub: null as Unsubscribe | null,
    _taskUnsubs: new Map<string, Unsubscribe>(),
    /**
     * Legacy tasks whose source has not answered yet, and whether the task
     * collection itself has. Loading ends when both are settled.
     *
     * It used to end when `_resolvedTasks.size >= _taskUnsubs.size`, which
     * counts ordinary tasks (resolved at once, no listener) against legacy
     * sources still in flight: a project holding both reported loaded before
     * its legacy cards arrived, and the cross-project board showed its count,
     * then grew by those cards a moment later (MYAC-1683).
     */
    _sourcesPending: new Set<string>() as Set<string>,
    _tasksSnapshotArrived: false as boolean,
    _currentUserUid: '' as string,
    loadedAssessments: [] as Array<{ id: string; name: string; created_at: Date | null; completeness: 'full' | 'partial' | null }>,
    filters: { ...DEFAULT_KANBAN_FILTERS } as KanbanFiltersState,
    /** Set of subject paths that the user has UNCHECKED (all others are implicitly checked). */
    subjectFilterUnchecked: new Set<string>() as Set<string>,
    /** Map taskId → paths of subject docs the task belongs to (denormalized from ProjectTask). */
    _taskSubjectRefs: new Map<string, string[]>() as Map<string, string[]>,
    /** Cache of fetched TaskGroupingSubject docs, keyed by full subject path. */
    _subjectDocsCache: new Map<string, TaskGroupingSubject>() as Map<string, TaskGroupingSubject>,
    /** Paths currently being fetched (dedup). */
    _subjectDocsFetching: new Set<string>() as Set<string>,
    /** Paths that failed to fetch (missing doc) — ignored silently going forward. */
    _subjectDocsOrphans: new Set<string>() as Set<string>,
    /**
     * taskId → `last_revision_seq`, the OCC anchor every command needs (§5.1).
     *
     * Kept here because the ProjectTask snapshot is the only place it arrives,
     * and a command has to carry it at submit time. Absent means 0 — a task
     * written before phase 0.2, or one that has never been revised.
     */
    _revisionSeqs: new Map<string, number>() as Map<string, number>,
    /**
     * Skip the board's own filter data — assessment names and grouping
     * subjects — for a board whose filters do not use them.
     *
     * The cross-project board (MYAC-1268) runs one of these stores per project
     * and filters by project, priority and assignee only. Fetching every
     * project's assessments and subject documents for filters it never shows
     * would multiply reads by the number of projects, for nothing.
     */
    _lite: false as boolean,
    /**
     * The project's task listener failed — as distinct from answering empty.
     *
     * Both end loading, and until now nothing told them apart. A board that
     * spans several projects (MYAC-1268) must say it is incomplete when one of
     * them could not be read, rather than show that project as having no work.
     */
    loadFailed: false as boolean,
    /**
     * Every task in the project, by id — INCLUDING the ones that are not on the
     * board (4.8a, MYAC-1028).
     *
     * `_resolvedTasks` cannot answer this question: it holds only the tasks
     * whose SOURCE resolved. `_subscribeToSourceTask` writes an entry when it
     * finds the proposal a task came from, so a task whose source was deleted,
     * or whose `proposal_locator` this build cannot route, is simply absent —
     * and an Action Plan row joining against it would show that task as
     * undecided and offer to accept it again.
     *
     * **The original justification here was retired by 5.3 (MYAC-1078 item 9).**
     * It said the snapshot handler "drops anything failing `isOnBoard`" and that
     * an archived task is "off the board". Both stopped being true when the
     * Inactive column shipped: that predicate no longer exists, the subscription
     * drops nothing on that basis, and archived tasks are on the board now — in
     * Inactive. The FIELD is still needed for the reason above, which is why
     * this is a rewrite rather than a deletion.
     *
     * Populated from the same collection snapshot as `_revisionSeqs`, on the
     * every-task side of that branch, so it costs no additional read or listener.
     */
    _projectTasks: new Map<string, ProjectTask>() as Map<string, ProjectTask>,
    /**
     * The board copies of the `parked` tasks (MYAC-1639), kept OUT of
     * `_resolvedTasks`.
     *
     * A parked task is the discussion of an action nobody has added yet: it must
     * never reach the Kanban, the `/tasks` page, the filters, the empty state or
     * the export, and every one of those reads `_resolvedTasks`. Keeping it in a
     * map of its own is what makes that one decision rather than five filters.
     * It is still a full `Task`, so the review screen's title, priority and
     * assignee edits go through the same optimistic field edits as the board's.
     */
    _parkedTasks: new Map<string, Task>() as Map<string, Task>,
    /**
     * Which content fields someone edited on each parked task, read off its
     * `content_edit` revisions, with the seq they were read at (MYAC-1639).
     *
     * Accepting a parked task keeps those fields and takes the engine's current
     * text for every other one, so the card shows exactly that: the row's own
     * values, overlaid with these. Re-read when the task's seq moves.
     */
    _parkedEdits: new Map<string, { seq: number; fields: ReadonlySet<string> }>() as Map<
      string,
      { seq: number; fields: ReadonlySet<string> }
    >,
    /**
     * The conflict a `stale` refusal is waiting for the user to resolve (§5.1).
     *
     * Held in the store rather than the board because the refusal arrives
     * asynchronously, long after the drag that caused it. `null` when there is
     * nothing to ask about.
     */
    /**
     * Whether the Inactive column is on the board (MYAC-1078 item 8).
     *
     * A RENDER filter and nothing more: `tasksByColumn` and `columnCounts`
     * below keep bucketing every column regardless, so the count is right the
     * moment the column appears rather than filling in a tick later.
     *
     * In the store rather than local to `KanbanBoard` because MYAC-1079 needs
     * to force it on: a link that targets a task sitting in a hidden column has
     * to reveal the column instead of reporting the task missing.
     */
    showInactiveColumn: false,
    taskConflict: null as TaskConflict | null,
  }),

  getters: {
    allAcceptedTasks(state): Task[] {
      return Array.from(state._resolvedTasks.values())
    },

    /**
     * A board task by its id — the deep link's resolver (MYAC-1079).
     *
     * `_resolvedTasks` is keyed by task id, and §7.3 makes that the PROPOSAL's
     * id too: a task inherits it at accept. So a link carrying only the id is
     * enough, and the pid comes from the route.
     *
     * Deliberately not "build `projects/{pid}/tasks/{id}` and call
     * `taskByPath`": a board task's `path` is its SOURCE path, not the kanban
     * one — which is why `onNavigateToQuestion` can regex an assessment id out
     * of it — so that lookup would never match.
     *
     * `null` for an id this project has no task for: the task is gone, or the
     * link came from somewhere the user cannot see. The caller says so rather
     * than showing an empty board.
     */
    taskById: (state) => (taskId: string | null | undefined): Task | null => {
      if (!taskId) return null
      return state._resolvedTasks.get(taskId) ?? null
    },

    /**
     * {@link taskById}, falling back to a `parked` task (MYAC-1639) — for the
     * action-plan surfaces, which show an undecided action's hidden task (its
     * comments, history, title and priority) while the board never does.
     * Deliberately not `taskById` itself: that one resolves deep links, and a
     * link to a task nobody added must read « not found ».
     */
    reviewTaskById: (state) => (taskId: string | null | undefined): Task | null => {
      if (!taskId) return null
      return state._resolvedTasks.get(taskId) ?? state._parkedTasks.get(taskId) ?? null
    },

    /** The content fields someone edited on this parked task — see `_parkedEdits`. */
    parkedEditsFor: (state) => (taskId: string | null | undefined): ReadonlySet<string> =>
      (taskId ? state._parkedEdits.get(taskId)?.fields : undefined) ?? NO_EDITS,

    /**
     * This project's board has finished its first load — or failed it.
     *
     * `isLoading` alone cannot say that: it is also `false` BEFORE a load starts,
     * so a reader that runs ahead of `loadKanbanTasks` (a page that loads in
     * `onMounted`, after its children's setup) took "not started" for "done" and
     * reported a linked task as missing before a single document was read
     * (MYAC-1418). Pinning the project closes that window, and a previous
     * project's finished load cannot answer for this one.
     */
    isLoadedFor: (state) => (pid: string | null | undefined): boolean =>
      !!pid && state._current_pid === pid && !state.isLoading,

    taskByPath: (state) => (path: string | null | undefined): Task | null => {
      if (!path) return null
      for (const task of state._resolvedTasks.values()) {
        if (task.path === path) return task
      }
      return null
    },

    filteredTasks(): Task[] {
      let tasks = this.allAcceptedTasks
      const f = this.filters

      if (f.priority !== 'all') {
        const filterKey = TASK_PRIORITY_TO_KEY[f.priority]
        tasks = tasks.filter((t) =>
          t.priority === f.priority || t.priority === filterKey,
        )
      }

      if (f.assignee === 'me') {
        const uid = this._currentUserUid
        // Empty uid = "My tasks" filter with no identity → show nothing rather
        // than fall through to "show all", which would silently mislabel the
        // filter state.
        tasks = uid ? tasks.filter((t) => t.review_assignee_uid === uid) : []
      } else if (f.assignee !== 'all') {
        tasks = tasks.filter((t) => t.review_assignee_uid === f.assignee)
      }

      if (f.assessmentIds.length > 0) {
        tasks = tasks.filter((t) => {
          const aid = taskAssessmentId(t)
          return aid ? f.assessmentIds.includes(aid) : false
        })
      }

      if (f.document !== 'all') {
        tasks = tasks.filter((t) => {
          const docs = t.isV2 ? t.documents_v2 : []
          return docs.some((d) => d.name === f.document)
        })
      }

      // K4 — provenance, read off the task's own `source` stamp. A task with no
      // stamp (manual, or written before the field existed) is filtered OUT
      // rather than kept: the filter's promise is "what this analysis produced",
      // and an unstamped task is not evidence that it was.
      if (f.sourceTrack !== 'all') {
        tasks = tasks.filter((t) => {
          if (t.source?.track !== f.sourceTrack) return false
          return !f.sourceGapId || t.source?.gap_id === f.sourceGapId
        })
      }

      if (this.subjectFilterUnchecked.size > 0) {
        const unchecked = this.subjectFilterUnchecked
        tasks = tasks.filter((t) => {
          const taskId = extractTaskId(t.path)
          if (!taskId) return true
          const refs = this._taskSubjectRefs.get(taskId)
          if (!refs || refs.length === 0) {
            const aid = taskAssessmentId(t)
            if (!aid) return true
            return !unchecked.has(otherSubjectKey(aid))
          }
          return refs.some((path) => !unchecked.has(path))
        })
      }

      return tasks
    },

    /**
     * The columns the board renders — every one, minus Inactive when it is off.
     *
     * Deliberately NOT used to build `tasksByColumn` or `columnCounts`: both
     * still cover `KANBAN_COLUMNS` in full, so hiding the column costs nothing
     * to compute and revealing it shows a correct number immediately.
     */
    visibleColumns(state): KanbanColumn[] {
      return KANBAN_COLUMNS.filter(
        (column) => column !== INACTIVE_COLUMN || state.showInactiveColumn,
      )
    },
    tasksByColumn(): Record<KanbanColumn, Task[]> {
      const result: Record<string, Task[]> = {}
      for (const col of KANBAN_COLUMNS) {
        result[col] = []
      }

      for (const task of this.filteredTasks) {
        // Lifecycle first, progress second — see `kanbanColumnFor`, which owns
        // that rule so this getter and `columnCounts` cannot disagree.
        const column = kanbanColumnFor(task)
        // `null` is the legacy `cancelled` progress and nothing else. Asserting
        // the column exists rather than testing `in result` is the difference
        // between an exclusion we chose and one that swallows any key we forgot
        // to seed.
        if (column !== null) {
          result[column].push(task)
        }
      }

      for (const col of KANBAN_COLUMNS) {
        result[col] = sortTasksByPriority(result[col])
      }

      return result as Record<KanbanColumn, Task[]>
    },

    columnCounts(): Record<KanbanColumn, number> {
      const byCol = this.tasksByColumn
      const counts: Record<string, number> = {}
      for (const col of KANBAN_COLUMNS) {
        counts[col] = byCol[col].length
      }
      return counts as Record<KanbanColumn, number>
    },

    availableDocumentNames(): string[] {
      const names = new Set<string>()
      for (const task of this.allAcceptedTasks) {
        const docs = task.isV2 ? task.documents_v2 : []
        for (const d of docs) {
          if (d.name) names.add(d.name)
        }
      }
      return Array.from(names).sort()
    },

    /**
     * Exactly the cards the board draws right now, in its column order.
     *
     * The count beside the filters and the Excel export both read this, so
     * neither can drift from the board. `filteredTasks` is not that set: it
     * still holds the Inactive cards while their column is hidden, and the
     * legacy `cancelled` tasks no column takes (MYAC-1335 — "20 tasks" beside
     * a board of 13).
     */
    visibleTasks(): Task[] {
      return this.visibleColumns.flatMap((column) => this.tasksByColumn[column])
    },

    /**
     * Every task the filters keep, drawn or not — hidden Inactive cards
     * included. Only the "no tasks yet" empty state reads it: a project whose
     * every task is inactive has tasks, and telling its user to accept some
     * would be wrong. The visible count is `visibleTasks.length`.
     */
    totalFilteredCount(): number {
      return this.filteredTasks.length
    },

    hasFilterableSubjects(): boolean {
      return this.visibleSubjectTree.length > 0
    },

    /**
     * Tree of assessments → subjects to render in the Kanban subject filter overlay.
     * Filters out subjects with 0 tasks currently accepted-and-not-done.
     * Respects the existing Assessment filter (loose coupling: if assessmentIds is
     * empty, all loaded assessments are included).
     */
    visibleSubjectTree(state): SubjectTreeGroup[] {
      const allowedAssessmentIds = state.filters.assessmentIds.length > 0
        ? new Set(state.filters.assessmentIds)
        : null

      const realCounts = new Map<string, { assessmentId: string; count: number; subject?: TaskGroupingSubject }>()
      const otherCounts = new Map<string, number>()

      for (const [taskId, refs] of state._taskSubjectRefs.entries()) {
        const task = state._resolvedTasks.get(taskId)
        if (!task) continue
        if (task.effectiveProgress === 'done') continue

        if (refs.length === 0) {
          const aid = taskAssessmentId(task)
          if (!aid) continue
          if (allowedAssessmentIds && !allowedAssessmentIds.has(aid)) continue
          otherCounts.set(aid, (otherCounts.get(aid) ?? 0) + 1)
          continue
        }

        for (const refPath of refs) {
          const aid = extractAssessmentIdFromSubjectPath(refPath) ?? ''
          if (allowedAssessmentIds && !allowedAssessmentIds.has(aid)) continue
          let entry = realCounts.get(refPath)
          if (!entry) {
            entry = { assessmentId: aid, count: 0, subject: state._subjectDocsCache.get(refPath) }
            realCounts.set(refPath, entry)
          }
          entry.count += 1
        }
      }

      const grouped = new Map<string, SubjectTreeItem[]>()

      for (const [subjectPath, entry] of realCounts.entries()) {
        if (!entry.subject) continue
        if (entry.count === 0) continue
        if (!grouped.has(entry.assessmentId)) grouped.set(entry.assessmentId, [])
        grouped.get(entry.assessmentId)!.push({
          kind: 'real',
          path: subjectPath,
          subject: entry.subject,
          count: entry.count,
        })
      }

      for (const [aid, count] of otherCounts.entries()) {
        if (count === 0) continue
        if (!grouped.has(aid)) grouped.set(aid, [])
        grouped.get(aid)!.push({ kind: 'other', path: otherSubjectKey(aid), count })
      }

      const sortedAssessments = [...state.loadedAssessments].sort((a, b) => {
        const aTime = a.created_at?.getTime() ?? 0
        const bTime = b.created_at?.getTime() ?? 0
        return bTime - aTime
      })

      const result: SubjectTreeGroup[] = []
      for (const a of sortedAssessments) {
        const subjects = grouped.get(a.id) ?? []
        if (subjects.length === 0) continue
        subjects.sort((x, y) => {
          if (x.kind === 'other') return 1
          if (y.kind === 'other') return -1
          return x.subject.order - y.subject.order
        })
        const date = a.created_at ? d(a.created_at, 'short') : ''
        const typeLabel = a.completeness ?? ''
        const labelParts = [a.name, date, typeLabel].filter(Boolean)
        result.push({
          assessmentId: a.id,
          assessmentLabel: labelParts.join(' - '),
          subjects,
        })
      }

      return result
    },
  },

  actions: {
    setCurrentUserUid(uid: string) {
      this._currentUserUid = uid
    },

    /** See `_lite`. Set before `loadKanbanTasks`. */
    setLite(lite: boolean) {
      this._lite = lite
    },

    async loadKanbanTasks(pid: string) {
      if (this._current_pid === pid && this._collectionUnsub) {
        return
      }

      this.unsubscribeAll()
      this._current_pid = pid
      this.isLoading = true
      this.loadFailed = false
      this.filters = kanbanFiltersForNewProject(this.filters)
      this.subjectFilterUnchecked = new Set<string>()

      const db = useFirestore()
      let initialLoad = true

      // IR-126 §7.2.2. A command this tab had in flight when it was last loaded
      // has nobody listening to it any more, so a failure would never reach
      // §4.5's dispatch — the user's edit would simply not be there, with no
      // message. Re-attaching here is the only thing that closes that.
      //
      // Not awaited: recovery narrates an outcome, and the board must not wait
      // on it to render. It never throws (see the module).
      void recoverPendingCommands(pid)

      this._collectionUnsub = onSnapshot(
        collection(db, `projects/${pid}/tasks`),
        (snapshot) => {
          for (const change of snapshot.docChanges()) {
            const projectTask = ProjectTask.fromFirebase(change.doc)
            if (!projectTask) continue

            const taskId = projectTask.id

            if (change.type === 'added' || change.type === 'modified') {
              // IR-126 §7.2. A command that timed out left an "unconfirmed"
              // notice on this card, anchored to the seq it was composed
              // against. This is the only place that learns the task moved
              // past it — the command's own listener gave up, which is why the
              // notice exists at all. Without this the notice clears only when
              // a later command applies or the user dismisses it, so a
              // teammate's edit would leave it up indefinitely.
              reconcilePending(pid, taskId, projectTask.revisionSeq)
              // The OCC anchor for any command on this task (§5.1). Read here
              // because the snapshot is the only place it arrives.
              // `.set` rather than replacing the Map: Pinia state collections
              // are reactive in Vue 3, so a copy per changed task makes a burst
              // of snapshot changes O(n²) in board size for no benefit.
              this._revisionSeqs.set(taskId, projectTask.revisionSeq)
              // 4.8a — every ProjectTask is kept here regardless of lifecycle.
              // `.set` on the existing Map for the same O(n²) reason the line
              // above gives.
              this._projectTasks.set(taskId, projectTask)

              // **5.3 deliberately reverses MYAC-989's removal — this is not a
              // regression.**
              //
              // MYAC-989 dropped every non-`active` task off the board, and it
              // was right to at the time: `task_unapprove` had just stopped
              // DELETING the Kanban copy (the trail has to survive), so a kept
              // task arrived as `modified` and would have sat among the active
              // ones **looking accepted** — there was no other way to render it.
              // Removing it was the only honest option available.
              //
              // §4.3 is the display source and always wanted otherwise: an
              // `unapproved` task shows greyed as "sent back" with a route to
              // re-approve, an `archived` one as retired. The Inactive column
              // is that treatment, so the reason for the removal is gone.
              //
              // Note MYAC-989's comment cited **§5.7** for this. That was a
              // misattribution — §5.7 is the lifecycle × operation permission
              // matrix and says nothing about the board. It does still govern
              // what a card may DO: `task_edit` is ✗ for both non-active
              // states, which is why an inactive card is not draggable.

              const refPaths = (projectTask.task_grouping_subject_refs ?? [])
                .map((r) => r.path)
                .filter((p): p is string => !!p)
              const nextRefs = new Map(this._taskSubjectRefs)
              nextRefs.set(taskId, refPaths)
              this._taskSubjectRefs = nextRefs
              for (const path of refPaths) {
                void this._fetchSubjectDoc(path)
              }
            }

            if (change.type === 'added') {
              if (projectTask.isLegacy && projectTask.task_ref?.path) {
                this._subscribeToSourceTask(db, taskId, projectTask.task_ref.path, pid)
              } else if (projectTask.name) {
                this._resolveFromProjectTask(taskId, projectTask)
              }
            } else if (change.type === 'modified' && !projectTask.isLegacy && projectTask.name) {
              this._resolveFromProjectTask(taskId, projectTask)
            } else if (change.type === 'modified' && projectTask.isLegacy) {
              // A legacy card renders from the proposal, so this branch used to
              // be dropped outright — and with it every change the apply layer
              // makes to the Kanban document. Retiring a legacy task wrote
              // `lifecycle: archived` here and the board went on showing the
              // card in To do, with no event left to tell it otherwise.
              //
              // Only the lifecycle is taken (see `_applyLifecycleToLegacy`), and
              // only onto a task the source subscription has already resolved:
              // if it has not landed yet, that handler reads `_projectTasks` —
              // set a few lines above — and picks this up on arrival.
              const resolved = this._resolvedTasks.get(taskId)
              if (resolved) {
                this._applyLifecycleToLegacy(resolved, taskId)
                this._resolvedTasks = new Map(this._resolvedTasks).set(taskId, resolved)
              }
            } else if (change.type === 'removed') {
              this._unsubscribeSourceTask(taskId)
              const nextRefs = new Map(this._taskSubjectRefs)
              nextRefs.delete(taskId)
              this._taskSubjectRefs = nextRefs
              // 4.8a — the document is gone, so the row has no task and its own
              // decision stands again. Left behind, the join would keep showing a
              // deleted task's state indefinitely.
              this._projectTasks.delete(taskId)
              this._dropParked(taskId)
              // A legacy task removed while its source was the last one in
              // flight leaves nothing else to end the load.
              if (this.isLoading) this._checkLoadingComplete()
            }
          }

          if (initialLoad) {
            initialLoad = false
            this._tasksSnapshotArrived = true
            if (snapshot.empty) {
              this.isLoading = false
            } else {
              this._checkLoadingComplete()
            }
          }
        },
        (error) => {
          logger.error('Error in project tasks collection listener', error)
          this.loadFailed = true
          this.isLoading = false
        },
      )
    },

    /**
     * The OCC anchor for a task (§5.1), or 0 when none has arrived.
     *
     * Exposed because un-approve lives in the assessments store and needs the
     * same anchor an edit does.
     *
     * **Zero covers two different things, and only one is a fact about the
     * task.** It is what `last_revision_seq` holds until the first edit — and
     * it is also what this returns when the snapshot has not landed yet.
     *
     * The second case is reachable. `loadKanbanTasks` is fired with `void` from
     * three stores, so a user acting on an already-edited task before that
     * snapshot arrives composes against 0, is refused `stale`, and is told the
     * task changed — about a task nobody touched. Narrow, because a
     * never-edited task holds 0 anyway and is immune; the exposure is exactly
     * the edited ones.
     *
     * Left as zero rather than made unrepresentable, because refusing to submit
     * on an unknown seq trades a rare wrong message for a rare dead button, and
     * the message at least sends the user to look at the task. Recorded so the
     * next reader does not take the fallback for a guarantee.
     */
    revisionSeqFor(taskId: string): number {
      return this._revisionSeqs.get(taskId) ?? 0
    },

    /**
     * The `ProjectTask` at this id, on the board or not — `null` when none exists.
     *
     * The Action Plan's half of the §7.3 join (4.8a): the task id IS the row id, so
     * a row asks for its own id and either gets the task whose state it displays or
     * `null`, which means the row's own stored decision stands.
     *
     * Reads `_projectTasks`, never `_resolvedTasks` — see that field for why the
     * board is the wrong source for this question.
     */
    projectTaskFor(taskId: string): ProjectTask | null {
      return this._projectTasks.get(taskId) ?? null
    },

    /**
     * Resolves `true` once the board holds the task at `taskPath`, `false` after
     * `timeoutMs` without it (MYAC-1557).
     *
     * A field edit composes against the board's copy of the task (`_taskAtPath`),
     * which arrives seconds after `task_create` is admitted — so an edit sent
     * right behind the create, like the action review's assignee, has to wait for
     * it rather than be refused as "no resolved task".
     *
     * `until` narrows "on the board" further: a revived task is on the board
     * while still sent back — and an accepted `parked` one is addressable before
     * it is (MYAC-1639) — and an edit composed before the acceptance lands would
     * carry a revision seq the acceptance has already moved past.
     */
    whenOnBoard(taskPath: string, until: () => boolean = () => true, timeoutMs = 30_000): Promise<boolean> {
      const ready = () => !!this._taskAtPath(taskPath) && until()
      if (ready()) return Promise.resolve(true)
      return new Promise((resolve) => {
        let stop: (() => void) | null = null
        const timer = setTimeout(() => {
          stop?.()
          resolve(false)
        }, timeoutMs)
        stop = watch(
          ready,
          (onBoard) => {
            if (!onBoard) return
            clearTimeout(timer)
            stop?.()
            resolve(true)
          },
        )
      })
    },

    _subscribeToSourceTask(db: ReturnType<typeof useFirestore>, taskId: string, taskPath: string, pid: string) {
      this._unsubscribeSourceTask(taskId)
      // Before the listener opens, so even a first answer from the cache finds it here.
      this._sourcesPending.add(taskId)

      const unsub = onSnapshot(
        doc(db, taskPath),
        (snapshot) => {
          if (!snapshot.exists()) {
            // The source is gone, so this legacy task has no content to render
            // from — it carries none of its own (`isLegacy` is `!name &&
            // !!task_ref`). It is simply not resolved: the board reads
            // `_resolvedTasks`, and returning here never adds it.
            //
            // **It used to delete the task document as well** (MYAC-1061). That
            // was the last client delete of `projects/{pid}/tasks/**`, which the
            // Client Data Contract does not allow: the apply layer owns this
            // collection, and a deleted task takes its revision trail with it.
            // `allow delete: if false` is live (applied by hand 2026-08-14), but
            // `repos/firebase` has not caught up — so `noClientTaskDelete.test.ts`
            // is the enforcement that travels with this code.
            // Removing it costs
            // nothing visible either, which is the part worth stating: the
            // delete was tidying Firestore, not preventing a blank card. The
            // card was already invisible by the time this line ran.
            //
            // What is left behind is an unreferenced document nothing renders.
            // The population is CLOSED — `apply_create` always writes `name`,
            // so no new legacy task can be created — and it only shrinks.
            logger.warn(`Source task ${taskId} no longer exists; leaving its task document`)
            this._unsubscribeSourceTask(taskId)
            // Re-check, as the error branch below does. Loading completes when
            // every source has answered; a gone source HAS answered, and
            // without this, one that answers last leaves the board loading
            // forever. Rare on one board — but the cross-project board waits
            // for every project, so one such task anywhere hung it (MYAC-1268).
            if (this.isLoading) this._checkLoadingComplete()
            return
          }

          const task = Task.fromFirebase(snapshot)
          if (task) {
            // The command target, which this path never derived (MYAC-1083).
            //
            // Its sibling `_resolveFromProjectTask` synthesises the same
            // pointer, and only ever ran for tasks that carry a `name` — so a
            // legacy task's `project_task_ref` was whatever the OLD PROPOSAL
            // happened to hold, which for anything accepted before the
            // back-pointer was stamped is nothing at all. The gap was harmless
            // until assignee/progress/priority became queued commands
            // (MYAC-978/980): those address a task by its Kanban path, so a task
            // that cannot produce one cannot be edited at all.
            //
            // Derived, not guessed: this subscription is started from the
            // `projects/{pid}/tasks` snapshot, so a ProjectTask at
            // `projects/{pid}/tasks/{taskId}` is guaranteed to exist — it is the
            // document that triggered this call.
            //
            // **The handler's `taskId`, never `task.id`.** The Task here is
            // deserialised from the SOURCE document, so its own id is the
            // proposal's, which need not equal the Kanban copy's.
            //
            // Only when absent, so a proposal that does carry a stamped pointer
            // keeps its own and a derived one can never overwrite a working
            // value with a different one.
            //
            // It also restores the History panel, which reads
            // `project_task_ref ?? path`: a legacy task's `path` is the source
            // path, which `splitTaskPath` declines, so the trail said "no
            // changes recorded yet" for every legacy task whatever its history.
            if (!task.project_task_ref) {
              task.project_task_ref = `projects/${pid}/tasks/${taskId}`
            }
            this._applyLifecycleToLegacy(task, taskId)

            const isNew = !this._resolvedTasks.has(taskId)
            this._resolvedTasks = new Map(this._resolvedTasks).set(taskId, task)
            this._sourcesPending.delete(taskId)

            if (this.isLoading) {
              this._checkLoadingComplete()
            } else if (isNew) {
              this._deriveAssessments()
            }
          }
        },
        (error) => {
          logger.error(`Error in source task listener for ${taskId}`, error)
          // Mirror _unsubscribeSourceTask: always release the handle before
          // dropping it from the map so listener teardown stays consistent.
          const u = this._taskUnsubs.get(taskId)
          u?.()
          this._taskUnsubs.delete(taskId)
          this._sourcesPending.delete(taskId)
          if (this.isLoading) {
            this._checkLoadingComplete()
          }
        },
      )

      this._taskUnsubs.set(taskId, unsub)
    },

    /**
     * Give a legacy task the lifecycle its Kanban document holds.
     *
     * **A legacy card renders from the PROPOSAL, and lifecycle does not live
     * there.** `Task.fromFirebase` above reads the source document, which
     * predates the field entirely — so `lifecycle` came back `undefined`,
     * `isActiveLifecycle` was permanently true, and `kanbanColumnFor` (which
     * decides lifecycle first) could only ever put the card in a progress
     * column. Retiring a legacy task therefore left it sitting in To do, which
     * is what the user saw: the command applied, the board could not show it.
     *
     * Only the lifecycle. The other review fields are the reverse case — a
     * legacy task's decision, progress and assignee were written on the source
     * document by the pre-Kanban flow, and its `ProjectTask` may carry none of
     * them, so overlaying those would blank a value the card is right to show.
     * Lifecycle has no such ambiguity: the source copy never had one, and the
     * apply layer is the only writer of the field.
     */
    _applyLifecycleToLegacy(task: Task, taskId: string) {
      const pt = this._projectTasks.get(taskId)
      if (!pt) return
      task.lifecycle = pt.lifecycle ?? undefined
    },

    _resolveFromProjectTask(taskId: string, pt: ProjectTask) {
      const task = new Task()
      // MYAC-719: the kanban ProjectTask is the source of truth for an accepted
      // task — point path at the kanban doc so all edits write here (the source
      // copy is frozen by rules). Keep the source assessment id for grouping.
      const kanbanPath = `projects/${this._current_pid}/tasks/${taskId}`
      Object.defineProperty(task, 'id', { get: () => taskId, configurable: true })
      Object.defineProperty(task, 'path', { get: () => kanbanPath, configurable: true })
      task.source_assessment_id = assessmentIdFromPath(pt.source_assessment_ref) ?? undefined
      task.source_assessment_ref = pt.source_assessment_ref ?? undefined
      task.schema_version = '2'
      task.name = pt.name ?? ''
      task.priority = pt.priority ?? ''
      task.category = pt.category ?? ''
      task.description = pt.description ?? ''
      task.context = pt.context ?? undefined
      task.clausesV2 = pt.clausesV2 ?? []
      task.documentsV2 = pt.documentsV2 ?? []
      task.sourceQuestions = pt.sourceQuestions ?? []
      // Carried explicitly like every field above it (MYAC-1093) — the list is
      // how a field reaches the board, and MYAC-995/996/999 each found a value
      // the migration dropped by not being on it.
      task.sourceRequirements = pt.sourceRequirements ?? []
      task.section_key = pt.section_key ?? undefined
      task.requirement_id = pt.requirement_id ?? undefined
      task.source_type = pt.source_type ?? undefined
      // MYAC-1166 — the AI stamp the dialog title's label reads. Same trap as
      // the fields around it: stamped in Firestore, exposed on the model, and
      // still absent on every card until it was put on this list (Aymeric's
      // verification, 2026-10-15: « the tag never shows on a hand-made task »).
      task.ai_edited = pt.ai_edited ?? undefined
      // K4 (MYAC-776) — the engine provenance the board filters on. The same
      // trap as the four fields around it: on the model, not on this list, so
      // absent on every card.
      task.source = pt.source ?? undefined
      // Carried explicitly, like every field above it. A board Task is built
      // field by field from the ProjectTask, so a field added to the model and
      // not to this list is silently absent on the board — which is how
      // MYAC-995, 996 and 999 each found a value the migration dropped.
      task.proposal_locator = pt.proposal_locator ?? undefined
      // 5.3 — the board needs it to pick a column. Dropped until now, which was
      // invisible only because MYAC-989 kept non-active tasks off the board.
      task.lifecycle = pt.lifecycle ?? undefined
      task.archived_from = pt.archived_from ?? undefined
      task.review_decision = pt.review_decision ?? undefined
      task.review_progress = pt.review_progress ?? undefined
      task.review_assignee_uid = pt.review_assignee_uid ?? undefined
      task.review_rejection_comment = pt.review_rejection_comment ?? undefined
      task.review_rejection_reason = pt.review_rejection_reason ?? undefined
      task.review_rejected_until = pt.review_rejected_until ?? undefined
      task.review_returned = pt.review_returned ?? undefined
      task.directive_id = pt.directive_id ?? undefined
      task.directive_match_reason = pt.directive_match_reason ?? undefined
      task.project_task_ref = `projects/${this._current_pid}/tasks/${taskId}`

      // MYAC-1639 — a parked task never joins the board; accepting it moves it
      // across (`parked → active` arrives as `modified`).
      if (pt.lifecycle === 'parked') {
        this._parkedTasks = new Map(this._parkedTasks).set(taskId, task)
        const known = this._parkedEdits.get(taskId)
        if (!known || known.seq !== pt.revisionSeq) {
          void this._refreshParkedEdits(taskId, pt.revisionSeq)
        }
        return
      }
      this._dropParked(taskId)
      // Retired before anyone added it (a re-assessment dropped its action): no
      // board, no Inactive column, nothing to address — the row decides alone.
      if (taskNeverAccepted(pt)) {
        if (this._resolvedTasks.has(taskId)) {
          const next = new Map(this._resolvedTasks)
          next.delete(taskId)
          this._resolvedTasks = next
        }
        return
      }

      const isNew = !this._resolvedTasks.has(taskId)
      this._resolvedTasks = new Map(this._resolvedTasks).set(taskId, task)

      if (this.isLoading) {
        this._checkLoadingComplete()
      } else if (isNew) {
        this._deriveAssessments()
      }
    },

    _dropParked(taskId: string) {
      if (this._parkedEdits.has(taskId)) {
        const edits = new Map(this._parkedEdits)
        edits.delete(taskId)
        this._parkedEdits = edits
      }
      if (!this._parkedTasks.has(taskId)) return
      const next = new Map(this._parkedTasks)
      next.delete(taskId)
      this._parkedTasks = next
    },

    /**
     * The content fields edited on a task, from its `content_edit` revisions —
     * every dotted path their `changes` name (MYAC-1639). `null` when the trail
     * could not be read, for the caller to pick its own safe answer.
     */
    async readEditedFields(projectId: string, taskId: string): Promise<Set<string> | null> {
      try {
        const snap = await getDocs(
          query(
            collection(useFirestore(), 'projects', projectId, REVISIONS_COLLECTION),
            where('entity_id', '==', taskId),
            where('entry_type', '==', 'content_edit'),
          ),
        )
        const fields = new Set<string>()
        for (const d of snap.docs) {
          for (const change of parseRevision(d.id, d.data()).changes) {
            if (change.field) fields.add(change.field)
          }
        }
        return fields
      } catch (e) {
        logger.error(`task ${taskId}: could not read its trail`, e)
        return null
      }
    },

    /**
     * The task at this id, read once — for the moment a create answered
     * `already_created` and the board has not shown that task yet (MYAC-1639).
     * `null` when it cannot be read.
     */
    async readProjectTask(projectId: string, taskId: string): Promise<ProjectTask | null> {
      try {
        return ProjectTask.fromFirebase(
          await getDoc(doc(useFirestore(), `projects/${projectId}/tasks/${taskId}`)),
        )
      } catch (e) {
        logger.error(`task ${taskId}: could not be read`, e)
        return null
      }
    },

    /** Re-read a parked task's edited fields at this seq. A failed read shows the
     *  engine's text, which is what accepting would keep had nothing been edited. */
    async _refreshParkedEdits(taskId: string, seq: number) {
      const pid = this._current_pid
      if (!pid) return
      const fields = (await this.readEditedFields(pid, taskId)) ?? new Set<string>()
      if (pid !== this._current_pid || !this._parkedTasks.has(taskId)) return
      const known = this._parkedEdits.get(taskId)
      // A newer read already landed, or an edit made here was marked meanwhile.
      if (known && known.seq > seq) return
      const merged = new Set([...fields, ...(known && known.seq === seq ? known.fields : [])])
      this._parkedEdits = new Map(this._parkedEdits).set(taskId, { seq, fields: merged })
    },

    /** An edit sent from here, shown at once — the trail confirms it on its next read. */
    markParkedEdited(taskId: string, fields: readonly string[]) {
      const known = this._parkedEdits.get(taskId)
      const merged = new Set([...(known?.fields ?? []), ...fields])
      this._parkedEdits = new Map(this._parkedEdits).set(taskId, {
        seq: known?.seq ?? -1,
        fields: merged,
      })
    },

    _unsubscribeSourceTask(taskId: string) {
      const unsub = this._taskUnsubs.get(taskId)
      if (unsub) {
        unsub()
        this._taskUnsubs.delete(taskId)
      }
      this._sourcesPending.delete(taskId)
      const updated = new Map(this._resolvedTasks)
      updated.delete(taskId)
      this._resolvedTasks = updated
    },

    _checkLoadingComplete() {
      if (this._tasksSnapshotArrived && this._sourcesPending.size === 0) {
        this.isLoading = false
        this._deriveAssessments()
      }
    },

    async _deriveAssessments() {
      if (this._lite) return
      const pid = this._current_pid
      if (!pid) return
      const db = useFirestore()
      const assessmentIds = new Set<string>()

      this._resolvedTasks.forEach((task) => {
        const aid = taskAssessmentId(task)
        if (aid) assessmentIds.add(aid)
      })

      // Fetch in parallel — previously O(n) sequential round-trips became
      // noticeable when many assessments feed the Kanban board.
      const fetches = Array.from(assessmentIds).map(async (aid) => {
        try {
          const assessmentDoc = await getDoc(doc(db, `projects/${pid}/assessments/${aid}`))
          if (!assessmentDoc.exists()) return null
          const data = assessmentDoc.data()
          return {
            id: aid,
            name: data.name || data.kind || aid,
            created_at: data.created_at?.toDate?.() ?? null,
            completeness: data.completeness ?? null,
          }
        } catch (error) {
          logger.error(`Error fetching assessment ${aid}`, error)
          return null
        }
      })

      const results = await Promise.all(fetches)
      const assessments = results.filter(
        (a): a is { id: string; name: string; created_at: Date | null; completeness: 'full' | 'partial' | null } =>
          a !== null,
      )

      this.loadedAssessments = assessments.sort((a, b) => {
        const aTime = a.created_at?.getTime() ?? 0
        const bTime = b.created_at?.getTime() ?? 0
        return bTime - aTime
      })
    },

    async _fetchSubjectDoc(subjectPath: string): Promise<void> {
      if (this._lite) return
      if (this._subjectDocsCache.has(subjectPath)) return
      if (this._subjectDocsFetching.has(subjectPath)) return
      if (this._subjectDocsOrphans.has(subjectPath)) return

      this._subjectDocsFetching.add(subjectPath)
      try {
        const db = useFirestore()
        const snap = await getDoc(doc(db, subjectPath))
        if (!snap.exists()) {
          this._subjectDocsOrphans.add(subjectPath)
          return
        }
        const subject = TaskGroupingSubject.fromFirebase(snap)
        if (subject) {
          this._subjectDocsCache = new Map(this._subjectDocsCache).set(subjectPath, subject)
        }
      } catch (error) {
        logger.warn(`Failed to fetch subject doc ${subjectPath}`, error)
        this._subjectDocsOrphans.add(subjectPath)
      } finally {
        this._subjectDocsFetching.delete(subjectPath)
      }
    },

    toggleSubjectFilter(subjectPath: string) {
      const next = new Set(this.subjectFilterUnchecked)
      if (next.has(subjectPath)) {
        next.delete(subjectPath)
      } else {
        next.add(subjectPath)
      }
      this.subjectFilterUnchecked = next
    },

    setSubjectFilterUnchecked(paths: Set<string>) {
      this.subjectFilterUnchecked = new Set(paths)
    },

    resetSubjectFilter() {
      this.subjectFilterUnchecked = new Set<string>()
    },

    /**
     * Send one field change through the task_edit queue (IR-126 §8, phase 3.4).
     *
     * The shared half of 3.4–3.6: they differ only in which field they set and
     * how it is labelled. What they share is everything that makes a queued
     * mutation safe to render optimistically —
     *
     * - the value goes on screen **before** the write, because the apply layer
     *   takes a second or two and the card must move under the cursor;
     * - it is put back if the command is refused, because the browser is no
     *   longer the writer and nothing else will correct it;
     * - a `stale` refusal opens the conflict rather than a toast (§5.1).
     *
     * Returns whether the change applied, so a caller can decide what else to
     * do — CSM tracking fires only on `done`, since counting an edit the apply
     * layer refused would make the metric a record of attempts.
     */
    async _submitFieldEdit(args: FieldEditArgs): Promise<boolean> {
      const { field, after, label, ...rest } = args
      return this._submitFieldsEdit({ ...rest, changes: [{ field, after, label }] })
    },

    /** {@link _submitFieldEdit} for a whole edit — see {@link FieldsEditArgs}. */
    async _submitFieldsEdit(args: FieldsEditArgs): Promise<boolean> {
      const { task, changes } = args
      const field = changes.map((c) => c.field).join(', ')
      const target = task.project_task_ref
      if (!target) {
        // A task with no ProjectTask cannot be commanded — the queue addresses
        // tasks by id under `projects/{pid}/tasks`. Legacy rows that never got
        // one are the only way here, and silently doing nothing would be the
        // vanished-edit failure this phase exists to remove.
        logger.error(`task ${task.id}: no project_task_ref, cannot submit ${field}`)
        const { useAppStore } = await import('./app')
        useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
        return false
      }

      const [, pid, , taskId] = target.split('/')
      const serverSeq = this.revisionSeqFor(taskId)

      try {
        return await this._runFieldEdit(args, pid, taskId, serverSeq, target)
      } catch (e) {
        // The old action wrapped everything in one try/catch, so any failure
        // reached the user as a friendly message. Narrowing that to the submit
        // promise alone would let a throw from `reportOutcome`, `label` or
        // `storedValue` become an unhandled rejection — the card back in its
        // old column and nothing said, which is the shape this phase exists to
        // remove.
        logger.error(`task ${taskId}: ${field} failed after submission`, e)
        const { useAppStore } = await import('./app')
        useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
        return false
      }
    },

    /** The half of {@link _submitFieldsEdit} that can throw. */
    async _runFieldEdit(
      args: FieldsEditArgs,
      pid: string,
      taskId: string,
      serverSeq: number,
      target: string,
    ): Promise<boolean> {
      const { changes, apply, rollback } = args
      const field = changes.map((c) => c.field).join(', ')

      /**
       * A top-level `undefined` is refused HERE rather than by Firestore.
       *
       * `toCommandValue` drops a nested key holding `undefined`, because such a
       * key carries no intent — `plainToInstance` assigns one for every absent
       * optional. At the top level there is no key to drop: `after` is required
       * by the payload shape, so the only choices are to pass it on or invent a
       * value, and inventing one would be wrong. `null` already MEANS something
       * on this path — `updateTaskAssignee` sends it to clear the field — so
       * coercing would turn a caller's mistake into a deliberate erasure
       * (review #532).
       *
       * `addDoc` would reject it anyway. What this adds is a message naming the
       * field, in our own code, instead of a Firestore error naming none. No
       * caller can reach it today; it exists so the first one that tries finds
       * out immediately rather than in a user's console.
       */
      const undefinedField = changes.find((c) => c.after === undefined)?.field
      if (undefinedField) {
        logger.error(
          `task ${taskId}: refusing to submit ${undefinedField} with an undefined value`,
        )
        const { useAppStore } = await import('./app')
        useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
        return false
      }

      const outcome = await submitPendingCommand(
        pid,
        {
          kind: 'task_edit',
          task_id: taskId,
          // Sanitised HERE because this is the one place every `task_edit`
          // passes through — progress, priority, assignee and content alike.
          // The other three only ever send primitives today, so nothing about
          // them changes; putting it here means a future caller that sends a
          // model object cannot reintroduce the bug (review #532). Fixing it
          // per-caller is what let the same failure land twice already.
          changes: changes.map(({ field: path, after }) => ({
            field: path,
            after: toCommandValue(after),
          })),
        },
        serverSeq,
        { optimistic: { apply, rollback } },
      ).catch(async (e) => {
        // The submission itself could not happen. Saying nothing here is the
        // same vanished-edit failure as saying nothing about a refusal — the
        // card goes back to its old column and the user is left to guess.
        //
        // Two rejections reach this, and they mean opposite things, which is
        // why `isNotQueued` exists (3.2b):
        //
        //   never written — not signed in, or the write failed. Nothing will
        //     ever apply it, so "your change was not saved" is simply true.
        //
        //   listener died after the write — the command IS queued and may well
        //     apply. Telling the user it was not saved would be a claim we
        //     cannot make, and the more dangerous direction to be wrong in
        //     (§7.2.1). They get the same "could not confirm" wording an
        //     unresolved command gets, because that is the honest state.
        logger.error(`task ${taskId}: ${field} could not be submitted`, e)
        const { useAppStore } = await import('./app')
        useAppStore().toast(
          t('common.error'),
          isNotQueued(e) ? t('tasks.errors.command.generic') : t('tasks.pending.unconfirmed_tooltip'),
          'error',
        )
        return null
      })

      if (!outcome) return false

      const verdict = reportOutcome(outcome)
      if (verdict.action === 'conflict') {
        // Read the task DIRECTLY, not from the resolved copy on the board.
        //
        // Two reasons, and the first is not subtle: by the time an outcome
        // reaches here `rollback()` has already run — `submitTaskCommand` calls
        // it inside `finish()` — so the local value is what this browser had
        // BEFORE the drag. A `stale` refusal means the task moved under us, so
        // that value is precisely the one thing the server does not hold. The
        // dialog would offer the user a choice between two values they already
        // knew, neither of them the conflicting one.
        //
        // Nor is the board's snapshot reliably fresher: the refusal arrives on
        // the command listener and the new value on the task listener, two
        // independent snapshots with no ordering guarantee — the same race
        // 3.2d documents for `serverSeq`. A direct read is the only thing that
        // answers "what does the task hold NOW".
        //
        // Resolved THROUGH the path, not by a single key (card 5.1). The three
        // fields 3.4–3.6 migrated are all top-level, so a flat lookup was
        // indistinguishable from a correct one; `context.finding` is a leaf on
        // a nested map, and a flat read returns `undefined` for it. The user
        // would then be asked to choose between their text and a blank — the
        // one thing the server definitely does not hold.
        const snap = await getDoc(doc(useFirestore(), target))
        const stored = snap.data() as Record<string, unknown> | undefined

        // Never auto-rebase: task-level comparison cannot tell whether the
        // concurrent change touched this field, so resubmitting unprompted
        // would overwrite a change we cannot see (§4.5).
        this.taskConflict = {
          taskId,
          // Every field the refused command carried. A single-field command is
          // the one-element case of the list, not a separate shape — which is
          // what keeps 3.4–3.6's three callers on exactly the path the editing
          // surface uses.
          fields: changes.map((c) => ({
            field: c.field,
            stored: c.label(readTaskPath(stored, c.field)),
            attempted: c.label(c.after),
          })),
          resubmit: async () => {
            this.taskConflict = null
            await this._submitFieldsEdit(args)
          },
        }
      }

      if (outcome.status === 'done') args.onApplied?.()
      return outcome.status === 'done'
    },

    /**
     * The board task a command path addresses — MYAC-1083.
     *
     * Two different paths legitimately name one task, and a caller may hold
     * either. A task resolved from its Kanban document has `path` ===
     * `project_task_ref`; a **legacy** task is deserialised from its SOURCE
     * document, so its `path` is the proposal's while the command target is the
     * Kanban one. `TaskDetailContent` hands over the write path — the target —
     * which for a legacy task matches no `path` at all, so a lookup on `path`
     * alone reported "no resolved task" for a task sitting on the board.
     *
     * The two can never name different tasks: a source path is never
     * Kanban-shaped, and both end in the same id (§7.3).
     */
    _taskAtPath(taskPath: string): Task | null {
      for (const task of this._resolvedTasks.values()) {
        if (task.path === taskPath || task.project_task_ref === taskPath) return task
      }
      // MYAC-1639 — an undecided action's hidden task takes title, priority and
      // assignee edits too; it is addressed, never shown on the board.
      for (const task of this._parkedTasks.values()) {
        if (task.project_task_ref === taskPath) return task
      }
      return null
    },

    /** Dismiss the conflict, leaving the stored value alone. */
    dismissTaskConflict() {
      this.taskConflict = null
    },

    async updateTaskProgress(taskPath: string, progress: TaskProgressStatus) {
      const task = this._taskAtPath(taskPath)
      if (!task) {
        // Same class as a missing project_task_ref twenty lines down, and it
        // deserves the same treatment: we cannot command a task the board does
        // not hold, and doing nothing quietly is the vanished-edit failure this
        // phase exists to remove.
        logger.error(`no resolved task at ${taskPath}, cannot move it`)
        const { useAppStore } = await import('./app')
        useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
        return
      }

      const previous = task.review_progress
      const pid = task.project_task_ref?.split('/')[1] ?? ''
      await this._submitFieldEdit({
        task,
        field: 'review_progress',
        after: progress,
        apply: () => {
          task.review_progress = progress
        },
        rollback: () => {
          task.review_progress = previous
        },
        // Column names, not enum keys — a conflict between "in_progress" and
        // "done" has to read the way the board reads.
        label: (v) => (v ? t(`tasks.columns.${String(v)}`) : t('tasks.columns.todo')),
        // Only once it applied, and via the callback so a move that succeeds
        // on a RESUBMIT after a conflict is tracked too. Tracking the attempt
        // would make TaskCompleted a count of drags rather than of tasks
        // finished; tracking only the first attempt would quietly lose every
        // move that took a conflict to resolve.
        onApplied: () => {
          if (!pid) return
          try {
            trackAction(CsmAction.TaskStatusChanged, {
              projectId: pid,
              projectMeta: { status: progress },
            })
          } catch {}
          if (progress === 'done') {
            try {
              trackAction(CsmAction.TaskCompleted, { projectId: pid })
            } catch {}
          }
        },
      })

    },

    async updateTaskPriority(taskPath: string, priority: string) {
      const task = this._taskAtPath(taskPath)
      if (!task) {
        // Same class as the missing task in updateTaskProgress, and it gets the
        // same treatment: we cannot command a task the board does not hold, and
        // doing nothing quietly is the vanished-edit failure this phase removes.
        logger.error(`no resolved task at ${taskPath}, cannot set its priority`)
        const { useAppStore } = await import('./app')
        useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
        return
      }

      const previous = task.priority
      await this._submitFieldEdit({
        task,
        field: 'priority',
        after: priority,
        apply: () => {
          task.priority = priority
        },
        rollback: () => {
          task.priority = previous
        },
        label: priorityLabel,
        // No onApplied: unlike a column move, a priority change has no CSM
        // action today. Adding one here would invent an event nobody asked
        // for; the callback exists for tracking that already existed.
      })
    },

    async updateTaskAssignee(taskPath: string, assigneeUid: string | null) {
      const task = this._taskAtPath(taskPath)
      if (!task) {
        logger.error(`no resolved task at ${taskPath}, cannot assign it`)
        const { useAppStore } = await import('./app')
        useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
        return
      }

      const previous = task.review_assignee_uid
      const pid = taskPath.split('/')[1] // taskPath = projects/{pid}/...
      await this._submitFieldEdit({
        task,
        field: 'review_assignee_uid',
        // `null` clears it, and FieldSpec accepts that deliberately — clearing
        // a field is a legitimate edit, not a malformed one.
        after: assigneeUid,
        apply: () => {
          task.review_assignee_uid = assigneeUid
        },
        rollback: () => {
          task.review_assignee_uid = previous
        },
        label: assigneeLabel,
        // Fire on both assign and unassign; `assigned: false` marks a clear.
        // Moved into the callback for the reason 3.4 established: an
        // assignment that only succeeds on a RESUBMIT after a conflict would
        // otherwise go untracked, and tracking the attempt instead would count
        // clicks rather than assignments.
        onApplied: () => {
          if (!pid) return
          try {
            trackAction(CsmAction.TaskAssigned, {
              projectId: pid,
              projectMeta: { assigned: assigneeUid != null },
            })
          } catch {}
        },
      })
    },

    /**
     * Send a content edit — every field the user changed, as one command (§4.4).
     *
     * Phase 5.1's write path, and the first caller that is not a single field.
     * `edits` is keyed by **stored leaf path** (`context.finding`), not by the
     * editor's own vocabulary: the mapping belongs to the surface that knows
     * the model, the same way `TaskDetailBody`'s neutral props do.
     *
     * Already filtered to what changed, by contract. A field the user retyped
     * identically would still append a revision, and a trail padded with
     * no-op revisions is a trail nobody reads — R1 asks for the before-value
     * of every change, not for every save.
     */
    async updateTaskContent(taskPath: string, edits: Record<string, unknown>): Promise<boolean> {
      const task = this._taskAtPath(taskPath)
      if (!task) {
        // Same class as the missing task in updateTaskProgress, same treatment.
        logger.error(`no resolved task at ${taskPath}, cannot edit its content`)
        const { useAppStore } = await import('./app')
        useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
        return false
      }

      const entries = Object.entries(edits)
      if (!entries.length) return true

      const board = task as unknown as Record<string, unknown>
      // The optimistic pair reads and writes the LOCAL property, which is not
      // always the path the command carries — see `optimisticPath`.
      const local = entries.map(([field]) => optimisticPath(field))
      const previous = local.map((field) => [field, readTaskPath(board, field)] as const)
      // Maps this edit will have to create, recorded before it creates them.
      // Putting only the leaves back on a refusal would leave an empty
      // `context` on a task that never had one — enough to flip
      // `hasStructuredContext`, and so which half of `bodyProps` renders.
      const created = [...new Set(local.map((field) => field.split('.')[0]))].filter(
        (root) => board[root] == null,
      )

      return this._submitFieldsEdit({
        task,
        // `labelForField`, not `textLabel`: since MYAC-1109 an edit can carry
        // `documents_v2` or `clauses_v2`, and `String([{...}])` renders
        // `[object Object]` — in the conflict dialog, where the whole point is
        // that the user can compare two values and choose one.
        changes: entries.map(([field, after]) => ({
          field,
          after,
          label: (value: unknown) => labelForField(field, value),
        })),
        apply: () => {
          // NOT sanitised: this writes into the resolved task, where the model's
          // own shape is what belongs — the snapshot rehydrates class instances
          // back anyway. Only the payload crossing to Firestore is flattened,
          // in `_runFieldEdit`.
          entries.forEach(([, after], i) => writeTaskPath(board, local[i], after))
        },
        rollback: () => {
          for (const [field, before] of previous) writeTaskPath(board, field, before)
          for (const root of created) delete board[root]
        },
        // No onApplied: editing content has no CSM action today, and the
        // callback exists for tracking that already existed (3.5's note).
      })
    },

    /**
     * Mark one action Done, or un-mark it — as a command (MYAC-1109).
     *
     * ## What this replaces
     *
     * `assessments.updateTaskDocumentValidated` did a `getDoc`, mutated the
     * list in memory and `updateDoc`'d it back — then wrote the SAME object to
     * `project_task_ref` in a second, best-effort `updateDoc` with an empty
     * catch. Three defects in nine lines, and the history gap was only the one
     * anybody had noticed:
     *
     * - **No trail.** No seq, no revision, no actor. MYAC-1097 removed the
     *   resolved-row guard on the promise that a Done row would be editable
     *   *because* the history would say who changed it. It did not.
     * - **A lost update.** Read-then-write with no transaction and no
     *   `base_seq`: two people ticking two different rows within a second of
     *   each other, and the second write carries a list read before the first
     *   landed. One tick vanishes, silently.
     * - **Two writers, one fact.** The task and its source copy were both
     *   written, the second inside `catch {}`. A failure there left the two
     *   documents disagreeing about which actions are done, permanently and
     *   invisibly.
     *
     * All three go away by routing through the same queue every other edit
     * uses. The source copy is deliberately NOT written any more: a proposal's
     * `documents_v2` is genesis content, and since MYAC-1095 every user action
     * on a linked proposal addresses the task.
     *
     * ## Whole list, because a leaf is refused
     *
     * `EditPolicy` matches exact-leaf, so `documents_v2.0.validated` is not
     * editable — a parent never grants its children. The command therefore
     * carries the whole `documents_v2`, with one element's `validated` flipped.
     *
     * ## No auto-rebase on `stale`, and the reason is sharper than usual
     *
     * §4.5's rule is that a refused command is never resubmitted unprompted,
     * because task-level comparison cannot tell whether the concurrent change
     * touched this field. For a list it looks like that objection lifts — the
     * stored list is right there to diff.
     *
     * It does not lift, because **the index is not an identity**. If the
     * concurrent change REMOVED a document, every index after it shifts, and
     * re-applying "index 3 is done" onto the new list marks a different
     * document — the exact silent-wrong-write the rule exists to prevent, and
     * worse than the lost update this method is fixing. `V2Document` has no
     * stable key to rebase on (`name` is neither unique nor immutable), so the
     * conflict goes to the user like every other one.
     *
     * The dialog is legible for it since MYAC-1109's labellers: it names the
     * documents and marks the done ones, where `textLabel` would have rendered
     * `[object Object],[object Object]`.
     */
    async updateTaskDocumentValidated(
      taskPath: string,
      documentIndex: number,
      value: boolean,
    ): Promise<boolean> {
      const task = this._taskAtPath(taskPath)
      if (!task) {
        logger.error(`no resolved task at ${taskPath}, cannot tick its document`)
        const { useAppStore } = await import('./app')
        useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
        return false
      }

      const documents = task.documents_v2 ?? []
      if (documentIndex < 0 || documentIndex >= documents.length) {
        // Out of range against the list this browser holds. Not a user error
        // and not worth a toast — the row cannot have been clicked unless it
        // rendered, so this is a snapshot that moved under the click.
        logger.warn(
          `task ${task.id}: document ${documentIndex} is out of range (${documents.length}), ignoring the tick`,
        )
        return false
      }

      // A new list, not a mutated one. `documents_v2` is read off the resolved
      // task, and `_submitFieldsEdit`'s rollback restores the value captured
      // BEFORE `apply()` — mutating in place would make the two the same array
      // and roll back to the new value.
      const next = documents.map((d, i) =>
        i === documentIndex ? { ...d, validated: value } : { ...d },
      )

      return this.updateTaskContent(taskPath, { documents_v2: next })
    },

    setFilters(patch: Partial<KanbanFiltersState>) {
      const oldAssessmentIds = new Set(this.filters.assessmentIds)
      this.filters = { ...this.filters, ...patch }

      if (patch.assessmentIds) {
        const newIds = new Set(patch.assessmentIds)
        const added = [...newIds].filter((id) => !oldAssessmentIds.has(id))
        if (added.length > 0) {
          const next = new Set(this.subjectFilterUnchecked)
          for (const path of [...next]) {
            const aid = extractAssessmentIdFromSubjectPath(path)
            if (aid && added.includes(aid)) {
              next.delete(path)
            }
          }
          this.subjectFilterUnchecked = next
        }
      }
    },

    resetFilters() {
      this.filters = { ...DEFAULT_KANBAN_FILTERS }
      this.subjectFilterUnchecked = new Set<string>()
    },

    unsubscribeAll() {
      if (this._collectionUnsub) {
        this._collectionUnsub()
        this._collectionUnsub = null
      }
      this._taskUnsubs.forEach((unsub) => {
        try {
          unsub()
        } catch (error) {
          logger.error('Error unsubscribing task listener', error)
        }
      })
      this._taskUnsubs = new Map()
      this._sourcesPending = new Set()
      this._tasksSnapshotArrived = false
      this._resolvedTasks = new Map()
      this._parkedTasks = new Map()
      this._parkedEdits = new Map()
      this.loadedAssessments = []
      this._current_pid = ''
      this.isLoading = false
      this._taskSubjectRefs = new Map()
      this._subjectDocsCache = new Map()
      this._subjectDocsFetching = new Set()
      this._subjectDocsOrphans = new Set()
      this.subjectFilterUnchecked = new Set()
    },
  },
  })
}

/** THE board store — a project's own Tasks page. */
export const useTasksStore = defineTasksStore('tasks')

export type TasksStore = ReturnType<typeof useTasksStore>

const projectBoardStores = new Map<string, ReturnType<typeof defineTasksStore>>()

/**
 * A board store for one project, for the cross-project board. Same code as
 * `useTasksStore`, separate instance — so it never disturbs the project page's
 * own board, and several can be live at once.
 */
export function useProjectBoardStore(projectId: string): TasksStore {
  const id = `tasks:project:${projectId}`
  let definition = projectBoardStores.get(id)
  if (!definition) {
    definition = defineTasksStore(id)
    projectBoardStores.set(id, definition)
  }
  return definition()
}

/**
 * Tear a project board store down: its listeners, then the instance itself —
 * a store left registered would keep its state for the life of the tab.
 */
export function disposeProjectBoardStore(store: TasksStore) {
  store.unsubscribeAll()
  store.$dispose()
  const pinia = getActivePinia()
  if (pinia) delete pinia.state.value[store.$id]
  // And the definition: kept, it would accumulate one per project ever
  // opened, for the life of the tab.
  projectBoardStores.delete(store.$id)
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTasksStore, import.meta.hot))
}
